"use client";

import { Heart } from "lucide-react";
import { useRouter } from "next/navigation";
import React, { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useAuth } from "@/hooks/use-auth";
import { useCartEnabled } from "@/hooks/use-feature-flags";
import { useWishlist } from "@/hooks/use-wishlist";
import { cn } from "@/lib/utils";
import { buildLoginUrl } from "@/utils/routes";

type WishlistButtonProps = {
  productId: string;
  variantId: string;
  productName?: string;
  isInWishlist?: boolean;
  className?: string;
  variant?: "default" | "outline" | "ghost" | "icon";
  size?: "default" | "sm" | "lg" | "icon";
  showText?: boolean;
  onWishlistChange?: (productId: string, isInWishlist: boolean) => void;
};

function WishlistButtonInner({
  productId,
  variantId,
  productName,
  isInWishlist = false,
  className,
  variant = "ghost",
  size = "icon",
  showText = false,
  onWishlistChange,
}: WishlistButtonProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const router = useRouter();
  const isCartEnabled = useCartEnabled();

  const { has, toggle, loading: wishlistLoading } = useWishlist();

  const wishlistState = has(productId, variantId) || isInWishlist;
  const isDisabled = (authLoading ?? false) || wishlistLoading;

  const buttonVariant = useMemo(
    () => (variant === "icon" ? "ghost" : variant),
    [variant]
  );

  const ariaLabel = useMemo(() => {
    if (!isAuthenticated) return "Sign in to add to wishlist";
    return wishlistState
      ? `Remove ${productName || "product"} from wishlist`
      : `Add ${productName || "product"} to wishlist`;
  }, [isAuthenticated, wishlistState, productName]);

  const statusText = useMemo(() => {
    if (wishlistLoading) return "Updating...";
    if (wishlistState) return "In Wishlist";
    return "Add to Wishlist";
  }, [wishlistLoading, wishlistState]);

  const handleWishlistToggle = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isAuthenticated) {
        const currentPath = window.location.pathname + window.location.search;
        const loginUrl = buildLoginUrl(currentPath);
        const absolute = new URL(loginUrl, window.location.origin);
        router.push(
          (absolute.pathname +
            absolute.search) as unknown as import("next").Route<string>
        );
        return;
      }

      try {
        await toggle(productId, variantId);
        onWishlistChange?.(productId, !wishlistState);
        toast.success(
          wishlistState
            ? `${productName || "Product"} removed from wishlist`
            : `${productName || "Product"} added to wishlist`
        );
      } catch (_e) {
        toast.error("Failed to update wishlist");
      }
    },
    [
      isAuthenticated,
      productId,
      variantId,
      productName,
      router,
      toggle,
      wishlistState,
      onWishlistChange,
    ]
  );

  // Don't render the button if cart functionality is disabled
  if (!isCartEnabled) {
    return null;
  }

  return (
    <Button
      aria-label={ariaLabel}
      className={cn(
        "transition-colors",
        wishlistState && "text-red-600 hover:text-red-700",
        className
      )}
      disabled={isDisabled}
      onClick={handleWishlistToggle}
      size={size}
      suppressHydrationWarning
      variant={buttonVariant}
    >
      {wishlistLoading ? (
        <LoadingSpinner className={showText ? "mr-2" : ""} size="sm" />
      ) : (
        <Heart
          className={cn(
            "h-4 w-4",
            showText && "mr-2",
            wishlistState && "fill-current"
          )}
          suppressHydrationWarning
        />
      )}
      {showText && <span>{statusText}</span>}
    </Button>
  );
}

export const WishlistButton = React.memo(WishlistButtonInner);

function WishlistButtonCompactInner({
  productId,
  variantId,
  productName,
  isInWishlist = false,
  className,
  onWishlistChange,
}: Pick<
  WishlistButtonProps,
  | "productId"
  | "variantId"
  | "productName"
  | "isInWishlist"
  | "className"
  | "onWishlistChange"
>) {
  return (
    <div className={cn("absolute top-2 right-2", className)}>
      <WishlistButton
        className="h-8 w-8 rounded-full bg-white/90 shadow-sm hover:bg-white"
        isInWishlist={isInWishlist}
        onWishlistChange={onWishlistChange}
        productId={productId}
        productName={productName}
        size="icon"
        variant="ghost"
        variantId={variantId}
      />
    </div>
  );
}

export const WishlistButtonCompact = React.memo(WishlistButtonCompactInner);

export default WishlistButton;
